import { ChangeDetectionStrategy, Component, Input, OnDestroy, signal, ViewChild, input, ChangeDetectorRef } from "@angular/core";
import { IonicModule, LoadingController, ModalController, isPlatform } from "@ionic/angular";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { ModeService } from "src/app/utils/mode.service";
import { InvoiceAddRequest } from "../../interfaces/invoice_add_request";
import { PaymentAddRequest } from "../../interfaces/payment_add_request";
import { Store } from "@ngrx/store";
import {
  addInvoice,
  downloadPdf,
} from "../../invoice-ngrx/invoices.actions";
import { addPayment } from "../../payment-ngrx/payments.actions";
import { HammerModule } from "@angular/platform-browser";
import { MailDialogComponent } from "../mail-dialog/mail-dialog.component";
import { PinchZoomComponent } from "@meddv/ngx-pinch-zoom";
import { formatDate } from "../../../utils/DateUtils";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { Subject, takeUntil } from "rxjs";
import * as invoiceSelector from "../../invoice-ngrx/invoices.selectors";
import * as paymentSelector from "../../payment-ngrx/payments.selectors";
import { Base64Service } from "src/app/shared/services/base64.service";

@Component({
    selector: "app-pdf-preview",
    templateUrl: "./pdf-preview.component.html",
    styleUrls: ["./pdf-preview.component.scss"],
    imports: [
        PdfViewerModule,
        IonicModule,
        HammerModule,
        PdfViewerModule,
        PinchZoomComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PdfPreviewComponent implements OnDestroy {
  constructor(
    public modeService: ModeService,
    private modalController: ModalController,
    private store: Store<any>,
    private loadingCtrl: LoadingController,
    private base64Service: Base64Service,
    private cdr: ChangeDetectorRef
  ) {
    const today = new Date();
    this.formattedDate.set(formatDate(today));
    this.isSharePluginAvailable.set(Capacitor.isPluginAvailable('Share') && Capacitor.isNativePlatform());
  }

  @ViewChild("pdfViewer") pdfViewer;
  readonly pdfSrc = input(undefined);
  readonly fileName = input(undefined);
  readonly invoiceAddRequest = input<InvoiceAddRequest>(undefined);
  readonly paymentAddRequest = input<PaymentAddRequest>(undefined);
  isDesktop = isPlatform("desktop");
  formattedDate = signal(null);
  isSharePluginAvailable = signal(Capacitor.isPluginAvailable('Share') && Capacitor.isNativePlatform());
  isGeneratingInvoice$ = this.store.select(invoiceSelector.selectIsLoading);
  isGeneratingPayment$ = this.store.select(paymentSelector.selectIsLoading);
  destroy$ = new Subject<void>();

  ionViewDidEnter() {
    this.cdr.markForCheck();
  }

  async cancel() {
    if (await this.modalController.getTop()) {
      this.modalController.dismiss();
    }
  }

  async downloadPdf() {
    var base64 = await this.base64Service.convertToBase64(this.pdfSrc());

    const fileNameValue = this.fileName();
    const invoiceAddRequest = this.invoiceAddRequest();
    let fileName = fileNameValue ? fileNameValue : invoiceAddRequest ? `Rechnung_${this.formattedDate()}_${invoiceAddRequest.student.surname}.pdf` : `Zahlungsbestätigung_${this.formattedDate}_${this.paymentAddRequest().student.surname}.pdf`
    // display the action to download the PDF
    this.store.dispatch(downloadPdf({ pdf: base64, fileName: fileName }));

    // also download the file using an anchor tag
    const link = document.createElement("a");
    link.href = this.pdfSrc();
    link.download = fileName;
    link.click();

    await this.save("download");
  }

  async openMailDialog() {
    var base64 = await this.base64Service.convertToBase64(this.pdfSrc());
    const modal = await this.modalController.create({
      component: MailDialogComponent,
      componentProps: {
        invoiceAddRequest: this.invoiceAddRequest,
        paymentAddRequest: this.paymentAddRequest,
        pdf: signal(base64),
        file: this.pdfSrc,
      },
    });
    await modal.present();

    await modal.onDidDismiss().then((data) => {
      if (data.data) {
        this.modalController.dismiss(true);
      }
    });
  }

  async save(status?: string) {
    const invoiceAddRequest = this.invoiceAddRequest();
    const paymentAddRequest = this.paymentAddRequest();
    if (invoiceAddRequest) {
      const loading = await this.loadingCtrl.create({
        message: "Rechnung abschliessen...",
      });
  
      loading.present();
      this.store.dispatch(
        addInvoice({
          invoice: invoiceAddRequest,
          status: status,
          file: this.pdfSrc(),
        })
      );

      this.isGeneratingInvoice$.pipe(takeUntil(this.destroy$)).subscribe(async (isLoading) => {
        if (!isLoading) {
          loading.dismiss();
          if ((await this.modalController.getTop()) !== undefined) {
            this.modalController.dismiss(true);
          }
        }
      })
    } else if (paymentAddRequest) {
      const loading = await this.loadingCtrl.create({
        message: "Zahlung abschliessen...",
      });
  
      loading.present();
      this.store.dispatch(
        addPayment({
          payment: paymentAddRequest,
        })
      );

      this.isGeneratingPayment$.pipe(takeUntil(this.destroy$)).subscribe(async (isLoading) => {
        if (!isLoading) {
          loading.dismiss();
          if ((await this.modalController.getTop()) !== undefined) {
            this.modalController.dismiss(true);
          }
        }
      })
    }
  }

  async sharePdf() {
    var base64 = await this.base64Service.convertToBase64(this.pdfSrc());
    return Filesystem.writeFile({
      path: this.fileName(),
      data: base64,
      directory: Directory.Cache
    })
      .then(() => {
        return Filesystem.getUri({
          directory: Directory.Cache,
          path: this.fileName()
        });
      })
      .then((uriResult) => {
        return Share.share({
          title: this.fileName(),
          text: this.fileName(),
          url: uriResult.uri,
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
